import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { del, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, useTempoRealTarefas } from './comum';
import type { Marco, Projeto } from './tipos';

const ESTADOS = ['pendentes', 'concluidos', 'todos'] as const;

/** Situação do marco: concluído, atrasado ou pendente (cor e texto). */
function SeloMarco({ m }: { m: Pick<Marco, 'concluido_em' | 'atrasado'> }) {
  const { t } = useTranslation('tarefas');
  if (m.concluido_em) return <Selo cor="verde">{t('marcos.concluido')}</Selo>;
  if (m.atrasado) return <Selo cor="perigo">{t('kanban.atrasado')}</Selo>;
  return <Selo cor="azul">{t('marcos.pendente')}</Selo>;
}

/** Criar (com o projeto escolhido ou fixo) ou editar o marco. */
function FormMarco({ marco, quadroId, aoFechar }: { marco: Marco | null; quadroId: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('tarefas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [id] = useState(() => marco?.id ?? crypto.randomUUID());
  const projetos = useQuery({ queryKey: [...CHAVE, 'projetos', 'opcoes'], queryFn: () => getTodos<Projeto>('/tarefas/projetos', { estado: 'ativos' }), enabled: !marco && !quadroId });
  const situacao = useMutation({
    mutationFn: (acao: 'concluir' | 'reabrir') => post(`/tarefas/marcos/${marco!.id}/${acao}`),
    onSuccess: async (_r, acao) => {
      toast.sucesso(t(acao === 'concluir' ? 'marcos.concluidoAviso' : 'marcos.reabertoAviso'));
      await qc.invalidateQueries({ queryKey: CHAVE });
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const campos: DefCampo[] = [
    ...(!marco && !quadroId
      ? [{ nome: 'quadro_id', rotulo: t('marcos.campos.projeto'), tipo: 'selecao', obrigatorio: true, opcoes: (projetos.data?.itens ?? []).map((p) => ({ valor: p.id, rotulo: p.nome })) } as DefCampo]
      : []),
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'data', rotulo: t('marcos.campos.data'), tipo: 'data', obrigatorio: true },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
  ];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={marco ? t('marcos.editar') : t('marcos.novo')}
      rodape={
        <>
          {/* No celular a linha da tabela é um botão só: concluir e reabrir também ficam aqui. */}
          {marco && (
            <Botao className="mr-auto" carregando={situacao.isPending} onClick={() => situacao.mutate(marco.concluido_em ? 'reabrir' : 'concluir')} icone={marco.concluido_em ? <RotateCcw className="size-4" /> : <Check className="size-4" />}>
              {t(marco.concluido_em ? 'marcos.reabrir' : 'marcos.concluir')}
            </Botao>
          )}
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-marco">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {projetos.isError ? (
        <Erro erro={projetos.error} aoTentar={() => projetos.refetch()} />
      ) : !marco && !quadroId && projetos.isPending ? (
        <Carregando />
      ) : !marco && !quadroId && !projetos.data?.itens.length ? (
        <Vazio texto={t('marcos.semProjetos')} />
      ) : (
        <Formulario
          id="form-marco"
          campos={campos}
          inicial={marco ? { nome: marco.nome, data: marco.data, descricao: marco.descricao ?? '' } : {}}
          aoEnviar={async (v) => {
            const corpo = { nome: v.nome, data: v.data, descricao: typeof v.descricao === 'string' && v.descricao.trim() ? v.descricao : null };
            if (marco) await patch(`/tarefas/marcos/${marco.id}`, corpo);
            else await post(`/tarefas/projetos/${quadroId ?? v.quadro_id}/marcos`, { id, ...corpo });
            await qc.invalidateQueries({ queryKey: CHAVE });
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        />
      )}
    </Modal>
  );
}

/** Concluir, reabrir e excluir o marco (mutações por clique, com toast de erro). */
function useAcoesMarco() {
  const { t } = useTranslation('tarefas');
  const qc = useQueryClient();
  const toast = useToast();
  const recarregar = () => qc.invalidateQueries({ queryKey: CHAVE });
  const situacao = useMutation({
    mutationFn: (v: { id: string; acao: 'concluir' | 'reabrir' }) => post(`/tarefas/marcos/${v.id}/${v.acao}`),
    onSuccess: async (_r, v) => {
      toast.sucesso(t(v.acao === 'concluir' ? 'marcos.concluidoAviso' : 'marcos.reabertoAviso'));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const excluir = useMutation({ mutationFn: (id: string) => del(`/tarefas/marcos/${id}`), onSuccess: recarregar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  return { situacao, excluir };
}

/** Marcos dentro da ficha do projeto: lista pela data, com concluir, editar, excluir e "Novo marco". */
export function MarcosDoProjeto({ projeto }: { projeto: Projeto }) {
  const { t } = useTranslation('tarefas');
  const s = useSessao();
  const podeEditar = s.pode('tarefas.editar') && !projeto.arquivado_em;
  const [form, setForm] = useState<Marco | 'novo' | null>(null);
  const [excluindo, setExcluindo] = useState<Marco | null>(null);
  const { situacao, excluir } = useAcoesMarco();
  const q = useQuery({ queryKey: [...CHAVE, 'marcos', projeto.id], queryFn: () => getTodos<Marco>('/tarefas/marcos', { quadro_id: projeto.id, estado: 'todos' }) });
  return (
    <section aria-labelledby={`marcos-${projeto.id}`} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`marcos-${projeto.id}`} className="text-sm font-semibold">
          {t('abas.marcos')}
        </h3>
        {podeEditar && (
          <Botao tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setForm('novo')}>
            {t('marcos.novo')}
          </Botao>
        )}
      </div>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : !q.data.itens.length ? (
        <p className="text-sm text-tinta-3">{t('marcos.vazioProjeto')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda">
          {q.data.itens.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <span className="flex min-w-0 grow basis-40 flex-col gap-0.5">
                <TextoCortado className={`text-sm font-medium ${m.concluido_em ? 'text-tinta-3 line-through' : ''}`}>{textoLivre(m.nome)}</TextoCortado>
                <span className="numero text-xs text-tinta-3">{data(m.data)}</span>
              </span>
              <span className="min-w-min">
                <SeloMarco m={m} />
              </span>
              {podeEditar && (
                <span className="flex items-center gap-1">
                  <Botao
                    tamanho="sm"
                    variante="fantasma"
                    aria-label={`${t(m.concluido_em ? 'marcos.reabrir' : 'marcos.concluir')}: ${textoLivre(m.nome)}`}
                    carregando={situacao.isPending && situacao.variables?.id === m.id}
                    onClick={() => situacao.mutate({ id: m.id, acao: m.concluido_em ? 'reabrir' : 'concluir' })}
                    icone={m.concluido_em ? <RotateCcw className="size-4" /> : <Check className="size-4" />}
                  />
                  <Botao tamanho="sm" variante="fantasma" aria-label={`${t('marcos.editar')}: ${textoLivre(m.nome)}`} onClick={() => setForm(m)} icone={<Pencil className="size-4" />} />
                  {s.pode('tarefas.excluir') && <Botao tamanho="sm" variante="fantasma" aria-label={`${t('marcos.excluir')}: ${textoLivre(m.nome)}`} onClick={() => setExcluindo(m)} icone={<Trash2 className="size-4" />} />}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {form && <FormMarco marco={form === 'novo' ? null : form} quadroId={projeto.id} aoFechar={() => setForm(null)} />}
      <Confirmar
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        aoConfirmar={() => excluindo && excluir.mutate(excluindo.id, { onSettled: () => setExcluindo(null) })}
        titulo={t('marcos.excluirTitulo')}
        texto={t('marcos.excluirTexto')}
        perigoso
        carregando={excluir.isPending}
      />
    </section>
  );
}

/** Aba Marcos (Pacote 2): os marcos de todos os projetos que a pessoa vê, pela data. */
export function Marcos() {
  const { t } = useTranslation('tarefas');
  const s = useSessao();
  useTempoRealTarefas();
  const [estado, setEstado] = useState<(typeof ESTADOS)[number]>('pendentes');
  const [projeto, setProjeto] = useState('');
  const [form, setForm] = useState<Marco | 'novo' | null>(null);
  const { situacao } = useAcoesMarco();
  const projetos = useQuery({ queryKey: [...CHAVE, 'projetos', 'opcoes'], queryFn: () => getTodos<Projeto>('/tarefas/projetos', { estado: 'ativos' }) });
  const colunas: Coluna<Marco>[] = [
    {
      chave: 'nome',
      rotulo: t('campos.nome'),
      principal: true,
      render: (m) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{textoLivre(m.nome)}</span>
          <SeloMarco m={m} />
        </span>
      ),
    },
    { chave: 'quadro_nome', rotulo: t('marcos.campos.projeto'), render: (m) => textoLivre(m.quadro_nome) },
    { chave: 'data', rotulo: t('marcos.campos.data'), mono: true, render: (m) => <span className={m.atrasado ? 'text-perigo' : ''}>{data(m.data)}</span> },
    { chave: 'concluido_em', rotulo: t('marcos.campos.concluidoEm'), render: (m) => (m.concluido_em ? `${dataHora(m.concluido_em)}${m.concluido_por_nome ? ` · ${nomePessoa(m.concluido_por_nome)}` : ''}` : '') },
    {
      chave: 'id',
      rotulo: t('marcos.campos.acao'),
      render: (m) =>
        s.pode('tarefas.editar') && !m.quadro_arquivado ? (
          <Botao
            tamanho="sm"
            variante="fantasma"
            carregando={situacao.isPending && situacao.variables?.id === m.id}
            onClick={(e) => {
              e.stopPropagation();
              situacao.mutate({ id: m.id, acao: m.concluido_em ? 'reabrir' : 'concluir' });
            }}
            icone={m.concluido_em ? <RotateCcw className="size-4" /> : <Check className="size-4" />}
          >
            {t(m.concluido_em ? 'marcos.reabrir' : 'marcos.concluir')}
          </Botao>
        ) : (
          ''
        ),
    },
  ];
  return (
    <>
      {s.pode('tarefas.editar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setForm('novo')}>
            {t('marcos.novo')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Marco>
        tela="tarefas.marcos"
        caminho="/tarefas/marcos"
        chaveConsulta={[...CHAVE, 'marcos', 'todos', estado, projeto]}
        params={{ estado, quadro_id: projeto || undefined }}
        filtros={{ estado, projeto }}
        aoAplicarFiltros={(f) => {
          setEstado(ESTADOS.find((x) => x === f.estado) ?? 'pendentes');
          setProjeto(typeof f.projeto === 'string' ? f.projeto : '');
        }}
        colunas={colunas}
        aoClicar={(m) => (s.pode('tarefas.editar') && !m.quadro_arquivado ? setForm(m) : undefined)}
        vazio={<Vazio titulo={t('marcos.vazioTitulo')} texto={t('marcos.vazioTexto')} />}
        filtrosExtra={
          <>
            <div className="w-full sm:w-44">
              <Selecao aria-label={t('campos.situacao')} opcoes={ESTADOS.map((x) => ({ valor: x, rotulo: t(`marcos.estados.${x}`) }))} value={estado} onChange={(e) => setEstado(e.target.value as (typeof ESTADOS)[number])} />
            </div>
            <div className="w-full sm:w-56">
              <Selecao
                aria-label={t('marcos.campos.projeto')}
                opcoes={(projetos.data?.itens ?? []).map((p) => ({ valor: p.id, rotulo: p.nome }))}
                vazio={`${t('marcos.campos.projeto')}: ${t('kanban.todos')}`}
                value={projeto}
                onChange={(e) => setProjeto(e.target.value)}
              />
            </div>
          </>
        }
      />
      {form && <FormMarco marco={form === 'novo' ? null : form} quadroId={form === 'novo' ? projeto || null : form.quadro_id} aoFechar={() => setForm(null)} />}
    </>
  );
}
